import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import type { PropsWithClassName } from '../../../types';
import { meta } from '../../constants';

const MIN_RESIZE_DELTA = 30;

interface WidthContext {
  width: number;
}

export const WidthContext = createContext<WidthContext>({
  width: 0
});

const { alias } = meta;

export function WidthProvider({
  className,
  children
}: PropsWithChildren<PropsWithClassName>) {
  const { widgetId } = useSettings();
  const [width, setWidth] = useState(0);
  const [measureRef] = useRect(
    ({ width: newWidth }) => {
      const resizeDelta = Math.abs(newWidth - width);
      if (resizeDelta > MIN_RESIZE_DELTA) {
        setWidth(newWidth);
      }
    },
    {
      resize: true
    }
  );

  const context = useMemo(
    () => ({
      width
    }),
    [width]
  );

  return (
    <WidthContext.Provider value={context}>
      <RootComponent
        id={`eapps-${alias}-${widgetId}`}
        ref={measureRef}
        className={className}
      >
        {children}
      </RootComponent>
    </WidthContext.Provider>
  );
}

export const useWidth = () => {
  const context = useContext(WidthContext);
  if (!context) {
    throw new Error(
      'The hook useWidth should be used only inside WidthProvider'
    );
  }

  return context;
};

const RootComponent = styled.div.attrs({
  className: 'RootLayout__RootComponent-sc'
})`
  width: 100%;
`;
